import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface PushStats {
  totalUsers: number;
  usersWithToken: number;
  usersWithTokenEnabled: number;
  usersWithoutToken: number;
}

interface BroadcastHistory {
  id: string;
  title: string;
  message: string;
  sentAt: string;
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

type SendMode = 'all' | 'single';

export default function AdminPushNotifications() {
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState<PushStats | null>(null);
  const [history, setHistory] = useState<BroadcastHistory[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendMode, setSendMode] = useState<SendMode>('all');

  // Form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');

  // Result state
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    sent?: number;
    failed?: number;
    total?: number;
    inAppCreated?: number;
  } | null>(null);

  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.role !== 'ADMIN') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };

      const [statsRes, historyRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/push-notifications/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/push-notifications/history`, { headers }),
        axios.get(`${API_URL}/api/admin/users`, { headers }),
      ]);

      setStats(statsRes.data);
      setHistory(historyRes.data.broadcasts || []);
      setUsers(usersRes.data.users || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);
    setError('');

    if (!title.trim() || !body.trim()) {
      setError('Titlul și mesajul sunt obligatorii');
      return;
    }

    if (sendMode === 'single' && !selectedUserId) {
      setError('Selectează un utilizator');
      return;
    }

    const confirmMsg =
      sendMode === 'all'
        ? `Ești sigur că vrei să trimiți notificarea la TOȚI utilizatorii (${stats?.usersWithTokenEnabled || 0} cu push activ)?`
        : `Trimiți notificarea la utilizatorul selectat?`;

    if (!confirm(confirmMsg)) return;

    setSending(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };

      if (sendMode === 'all') {
        const res = await axios.post(
          `${API_URL}/api/admin/push-notifications/send-all`,
          { title: title.trim(), body: body.trim() },
          { headers }
        );
        setResult(res.data);
      } else {
        const res = await axios.post(
          `${API_URL}/api/admin/push-notifications/send-user`,
          { userId: selectedUserId, title: title.trim(), body: body.trim() },
          { headers }
        );
        setResult(res.data);
      }

      // Reset form
      setTitle('');
      setBody('');
      setSelectedUserId('');
      setUserSearch('');

      // Refresh history
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la trimiterea notificării');
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    const search = userSearch.toLowerCase();
    return (
      u.email.toLowerCase().includes(search) ||
      `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(search)
    );
  });

  const selectedUser = users.find((u) => u.id === selectedUserId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/admin')}
              className="text-gray-500 hover:text-gray-700 flex items-center gap-1 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Admin Panel
            </button>
            <span className="text-gray-300">/</span>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🔔</span>
              <span className="font-semibold text-gray-900">Push Notifications</span>
            </div>
          </div>
          <div className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
            ADMIN
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Title */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📣 Trimite Notificări Push</h1>
          <p className="text-gray-500 mt-1">
            Trimite mesaje custom direct pe telefoanele utilizatorilor
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="text-xs text-gray-500 mb-1">Total Utilizatori</div>
              <div className="text-3xl font-bold text-gray-900">{stats.totalUsers}</div>
            </div>
            <div className="bg-blue-50 rounded-xl shadow-sm border border-blue-100 p-5">
              <div className="text-xs text-blue-600 mb-1">Cu Token Push</div>
              <div className="text-3xl font-bold text-blue-900">{stats.usersWithToken}</div>
            </div>
            <div className="bg-green-50 rounded-xl shadow-sm border border-green-100 p-5">
              <div className="text-xs text-green-600 mb-1">Push Activat ✅</div>
              <div className="text-3xl font-bold text-green-900">{stats.usersWithTokenEnabled}</div>
            </div>
            <div className="bg-gray-50 rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="text-xs text-gray-500 mb-1">Fără Token</div>
              <div className="text-3xl font-bold text-gray-700">{stats.usersWithoutToken}</div>
            </div>
          </div>
        )}

        {/* Send Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-indigo-600">
            <h2 className="text-lg font-bold text-white">✉️ Compune Notificarea</h2>
            <p className="text-blue-100 text-sm mt-0.5">
              Notificarea va fi trimisă push + salvată în inbox-ul in-app al utilizatorilor
            </p>
          </div>

          <form onSubmit={handleSend} className="p-6 space-y-5">
            {/* Send Mode Toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destinatari
              </label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => { setSendMode('all'); setSelectedUserId(''); setUserSearch(''); }}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                    sendMode === 'all'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  🌍 Toți Utilizatorii
                </button>
                <button
                  type="button"
                  onClick={() => setSendMode('single')}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors border-l border-gray-200 ${
                    sendMode === 'single'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  👤 Utilizator Specific
                </button>
              </div>
            </div>

            {/* User Search (single mode) */}
            {sendMode === 'single' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Caută Utilizator
                </label>
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => { setUserSearch(e.target.value); setSelectedUserId(''); }}
                  placeholder="Caută după email sau nume..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {userSearch && !selectedUserId && (
                  <div className="mt-1 border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto shadow-sm">
                    {filteredUsers.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-gray-500 text-center">
                        Nu s-au găsit utilizatori
                      </div>
                    ) : (
                      filteredUsers.slice(0, 10).map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => {
                            setSelectedUserId(u.id);
                            setUserSearch(`${u.firstName || ''} ${u.lastName || ''} (${u.email})`.trim());
                          }}
                          className="w-full text-left px-3 py-2.5 hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {u.firstName} {u.lastName}
                          </div>
                          <div className="text-xs text-gray-500">{u.email}</div>
                        </button>
                      ))
                    )}
                  </div>
                )}
                {selectedUser && (
                  <div className="mt-2 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                    <span className="text-blue-600 text-sm">✓</span>
                    <span className="text-sm text-blue-800 font-medium">
                      {selectedUser.firstName} {selectedUser.lastName} — {selectedUser.email}
                    </span>
                    <button
                      type="button"
                      onClick={() => { setSelectedUserId(''); setUserSearch(''); }}
                      className="ml-auto text-blue-400 hover:text-blue-600 text-xs"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titlu Notificare
                <span className="text-gray-400 font-normal ml-1">({title.length}/100)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
                placeholder="ex: 🎉 Noutăți FII FIT"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mesaj
                <span className="text-gray-400 font-normal ml-1">({body.length}/500)</span>
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="Scrie mesajul notificării..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                required
              />
            </div>

            {/* Preview */}
            {(title || body) && (
              <div className="bg-gray-900 rounded-xl p-4">
                <div className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Preview Notificare</div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      K+
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {title || 'Titlu notificare'}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                        {body || 'Mesajul notificării...'}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 flex-shrink-0">acum</div>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">❌ {error}</p>
              </div>
            )}

            {/* Success Result */}
            {result && (
              <div className={`rounded-lg p-4 border ${result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <p className={`font-semibold text-sm ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                  {result.success ? '✅' : '❌'} {result.message}
                </p>
                {result.success && result.total !== undefined && (
                  <div className="mt-2 grid grid-cols-3 gap-3">
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-700">{result.sent}</div>
                      <div className="text-xs text-green-600">Push trimise</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-orange-600">{result.failed}</div>
                      <div className="text-xs text-orange-500">Eșuate</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-700">{result.inAppCreated}</div>
                      <div className="text-xs text-blue-600">In-app create</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={sending || !title.trim() || !body.trim() || (sendMode === 'single' && !selectedUserId)}
              className={`w-full py-3 rounded-xl font-semibold text-white transition-all ${
                sending || !title.trim() || !body.trim() || (sendMode === 'single' && !selectedUserId)
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg'
              }`}
            >
              {sending ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Se trimite...
                </span>
              ) : sendMode === 'all' ? (
                `🚀 Trimite la Toți (${stats?.usersWithTokenEnabled || 0} utilizatori cu push activ)`
              ) : (
                '📤 Trimite Notificarea'
              )}
            </button>

            {sendMode === 'all' && (
              <p className="text-xs text-gray-400 text-center">
                ⚠️ Notificarea va fi trimisă push la {stats?.usersWithTokenEnabled || 0} utilizatori cu push activat
                și salvată în inbox-ul in-app al tuturor {stats?.totalUsers || 0} utilizatorilor activi.
              </p>
            )}
          </form>
        </div>

        {/* History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">📋 Istoric Broadcast-uri</h2>
            <p className="text-gray-500 text-sm mt-0.5">Ultimele notificări trimise la toți utilizatorii</p>
          </div>

          {history.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="text-4xl mb-3">📭</div>
              <p className="text-gray-500">Nu ai trimis nicio notificare broadcast încă</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {history.map((item) => (
                <div key={item.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {item.title}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 flex-shrink-0">
                          Broadcast
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{item.message}</p>
                    </div>
                    <div className="text-xs text-gray-400 flex-shrink-0 text-right">
                      <div>{new Date(item.sentAt).toLocaleDateString('ro-RO')}</div>
                      <div>{new Date(item.sentAt).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
